import { Check, X, Building, Clock, Users, Stethoscope, Upload, MapPin, FileText, Phone, Mail } from 'lucide-react';
import useProviderTypeStore from '../../store/providerTypeStore';
import useHealthAuthorityStore from '../../store/healthAuthorityStore';
import useEmiratesStore from '../../store/emiratesStore';
import useCityStore from '../../store/cityStore';
import useAreaStore from '../../store/areaStore';
import useRegistrationStore from '../../store/useRegistrationStore';
import useFacilityStore from '../../store/facilityStore';
import useSpecialtiesStore from '../../store/specialtiesStore';
import { toAmPm } from '../../helper/helper';

const ReviewStep = () => {
    // Get data from stores
    const {
        providerData,
        formData,
        authorityData,  // Now including authority data
        selectedServices,
        selectedSpecialities
    } = useRegistrationStore();

    const { providerTypes } = useProviderTypeStore();
    const { healthAuthorities } = useHealthAuthorityStore();
    const { emirates } = useEmiratesStore();
    const { cities } = useCityStore();
    const { areas } = useAreaStore();
    const { getFacilityNameById } = useFacilityStore();
    const { getSpecialityNameById } = useSpecialtiesStore();
    const isPharmacy = providerData?.providerType === 2;

    // Helper functions to get display names
    const getproviderTypeName = (id) => {
        const type = providerTypes.find(t => t.providerType_ID === parseInt(id));
        return type?.providerTypeName || '—';
    };

    const getHealthAuthorityName = (id) => {
        const ha = healthAuthorities.find(h => h.HealthAuthority_ID === parseInt(id));
        return ha?.HealthAuthority_Name || '—';
    };

    const getEmirateName = (id) => {
        const emirate = emirates.find(e => e.State_ID === parseInt(id));
        return emirate?.StateName || '—';
    };

    const getCityName = (id) => {
        const city = cities.find(c => c.City_ID === parseInt(id));
        return city?.CityName || '—';
    };

    const getAreaName = (id) => {
        const area = areas.find(a => a.Area_ID === parseInt(id));
        return area?.AreaName || '—';
    };



    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    return (
        <div className="space-y-8 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Check className="w-6 h-6 text-primary" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Final Review</h2>
                    <p className="text-base text-gray-600 mt-1">
                        Verify all details before submission
                    </p>
                </div>
            </div>

            {/* Review Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {/* 1. Facility Info */}
                <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 shadow-xl">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                            <Building className="w-6 h-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Facility Info</h3>
                    </div>
                    <dl className="space-y-4 text-base">
                        <div>
                            <dt className="text-sm text-gray-600">Type</dt>
                            <dd className="font-medium text-gray-900">
                                {getproviderTypeName(providerData.providerType)}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-600">Name</dt>
                            <dd className="font-medium text-gray-900">
                                {providerData.facilityName || '—'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-600">Health Authority</dt>
                            <dd className="font-medium text-gray-900">
                                {getHealthAuthorityName(providerData.healthAuthority)}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-600">Website</dt>
                            <dd className="font-medium text-gray-900">
                                {providerData.website || '—'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-600">Landline</dt>
                            <dd className="font-medium text-gray-900">
                                {providerData.landline || '—'}
                            </dd>
                        </div>
                        {providerData.isBranch && (
                            <div>
                                <dt className="text-sm text-gray-600">Main Branch HA Licence Number</dt>
                                <dd className="font-medium text-gray-900">
                                    {providerData.mainBranchHANumber || '—'}
                                </dd>
                            </div>
                        )}
                        <div>
                            <dt className="text-sm text-gray-600">Operating Hours</dt>
                            <dd className="font-medium text-gray-900 flex items-center gap-2">
                                <Clock size={16} className="text-gray-500" />
                                {toAmPm(providerData.openTime)} – {toAmPm(providerData.closeTime)}
                            </dd>

                        </div>
                    </dl>
                </div>

                {/* 2. Location Details */}
                <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 shadow-xl">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                            <MapPin className="w-6 h-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Location</h3>
                    </div>
                    <dl className="space-y-4 text-base">
                        <div>
                            <dt className="text-sm text-gray-600">Country</dt>
                            <dd className="font-medium text-gray-900">
                                United Arab Emirates
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-600">Emirate</dt>
                            <dd className="font-medium text-gray-900">
                                {getEmirateName(providerData.emirates)}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-600">City</dt>
                            <dd className="font-medium text-gray-900">
                                {getCityName(providerData.city)}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-600">Area</dt>
                            <dd className="font-medium text-gray-900">
                                {getAreaName(providerData.area)}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-600">Street Address</dt>
                            <dd className="font-medium text-gray-900">
                                {providerData.street || '—'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-600">Location</dt>
                            <dd className="font-medium text-gray-900">
                                {providerData.location || '—'}
                            </dd>
                        </div>
                    </dl>
                </div>

                {/* 3. Patient Capacity */}
                <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 shadow-xl">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                            <Users className="w-6 h-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">
                            {isPharmacy ? 'Pharmacy Discount' : 'Patient Capacity'}
                        </h3>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="text-center bg-gray-50 rounded-2xl py-4 px-3">
                            {formData.patientCapacityOP > 0 && (
                                <div>
                                    <div className="text-sm text-gray-600">Outpatient</div>
                                    <div className="text-2xl font-bold text-gray-900 mt-2">
                                        {formData.patientCapacityOP || '0'}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">per day</div>
                                </div>
                            )}

                        </div>

                        {!isPharmacy && (
                            <div className="text-center bg-gray-50 rounded-2xl py-4 px-3">
                                <div className="text-sm text-gray-600">Inpatient</div>
                                <div className="text-2xl font-bold text-gray-900 mt-2">
                                    {formData.patientCapacityIP || '0'}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">beds</div>
                            </div>
                        )}

                        {!isPharmacy && (
                            <div className="text-center bg-gray-50 rounded-2xl py-4 px-3">
                                <div className="text-sm text-gray-600">Day Care</div>
                                <div className="text-2xl font-bold text-gray-900 mt-2">
                                    {formData.patientCapacityDC || '0'}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">per day</div>
                            </div>
                        )}

                        {isPharmacy &&
                            <div className="text-center bg-gray-50 rounded-2xl py-4 px-3">
                                <div className="text-sm text-gray-600">Discount</div>
                                <div className="text-2xl font-bold text-gray-900 mt-2">
                                    {formData.discount || '0'}
                                </div>
                            </div>
                        }
                    </div>
                </div>

                {/* 4. License Information */}
                <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 shadow-xl md:col-span-2">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                            <FileText className="w-6 h-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">License Information</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-800 mb-3">Facility License</h4>
                            <dl className="space-y-3">
                                <div>
                                    <dt className="text-xs text-gray-600">License Number</dt>
                                    <dd className="font-medium text-gray-900">
                                        {authorityData.facilityLicenseNo || '—'}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-gray-600">Expiry Date</dt>
                                    <dd className="font-medium text-gray-900">
                                        {formatDate(authorityData.facilityLicenseExpiry)}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                        <div>
                            <h4 className="text-sm font-semibold text-gray-800 mb-3">Trade License</h4>
                            <dl className="space-y-3">
                                <div>
                                    <dt className="text-xs text-gray-600">License Number</dt>
                                    <dd className="font-medium text-gray-900">
                                        {authorityData.tradeLicNo || '—'}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-gray-600">License Name</dt>
                                    <dd className="font-medium text-gray-900">
                                        {authorityData.tradeLicName || '—'}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-gray-600">Expiry Date</dt>
                                    <dd className="font-medium text-gray-900">
                                        {formatDate(authorityData.tradeLicExpiry)}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>

                {/* 5. Authorized Signatory */}
                <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 shadow-xl">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                            <Users className="w-6 h-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Authorized Signatory</h3>
                    </div>
                    <dl className="space-y-4 text-base">
                        <div>
                            <dt className="text-sm text-gray-600">Name</dt>
                            <dd className="font-medium text-gray-900">
                                {authorityData.signatory1Name || '—'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-600">Designation</dt>
                            <dd className="font-medium text-gray-900">
                                {authorityData.signatory1Designation || '—'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-600">Emirates ID</dt>
                            <dd className="font-medium text-gray-900">
                                {authorityData.signatory1EmiratesId || '—'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm text-gray-600">Contact Number</dt>
                            <dd className="font-medium text-gray-900 flex items-center gap-2">
                                <Phone size={14} className="text-gray-500" />
                                {authorityData.signatory1ContactNo || '—'}
                            </dd>
                        </div>
                        {authorityData.signatory1Remarks && (
                            <div>
                                <dt className="text-sm text-gray-600">Remarks</dt>
                                <dd className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg">
                                    {authorityData.signatory1Remarks}
                                </dd>
                            </div>
                        )}
                    </dl>
                </div>

                {/* 6. Department Contacts */}
                <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 shadow-xl md:col-span-2 xl:col-span-3">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                            <Phone className="w-6 h-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Department Contacts</h3>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Network & Insurance */}
                        <div>
                            <h4 className="text-base font-semibold text-blue-600 mb-4">Network & Insurance</h4>
                            <dl className="space-y-3">
                                <div>
                                    <dt className="text-xs text-gray-600">Name</dt>
                                    <dd className="font-medium text-gray-900">
                                        {authorityData.networkInsuranceName1 || '—'}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-gray-600">Email</dt>
                                    <dd className="font-medium text-gray-900 flex items-center gap-2">
                                        <Mail size={14} className="text-gray-500" />
                                        <span className="truncate">{authorityData.networkInsuranceEmail1 || '—'}</span>
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-gray-600">Contact</dt>
                                    <dd className="font-medium text-gray-900 flex items-center gap-2">
                                        <Phone size={14} className="text-gray-500" />
                                        {authorityData.networkInsuranceContact1 || '—'}
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        {/* Accounts */}
                        <div>
                            <h4 className="text-base font-semibold text-emerald-600 mb-4">Accounts</h4>
                            <dl className="space-y-3">
                                <div>
                                    <dt className="text-xs text-gray-600">Name</dt>
                                    <dd className="font-medium text-gray-900">
                                        {authorityData.accountsName1 || '—'}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-gray-600">Email</dt>
                                    <dd className="font-medium text-gray-900 flex items-center gap-2">
                                        <Mail size={14} className="text-gray-500" />
                                        <span className="truncate">{authorityData.accountsEmail1 || '—'}</span>
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-gray-600">Contact</dt>
                                    <dd className="font-medium text-gray-900 flex items-center gap-2">
                                        <Phone size={14} className="text-gray-500" />
                                        {authorityData.accountsContact1 || '—'}
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        {/* Administration */}
                        <div>
                            <h4 className="text-base font-semibold text-purple-600 mb-4">Administration</h4>
                            <dl className="space-y-3">
                                <div>
                                    <dt className="text-xs text-gray-600">Name</dt>
                                    <dd className="font-medium text-gray-900">
                                        {authorityData.administrationName1 || '—'}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-gray-600">Email</dt>
                                    <dd className="font-medium text-gray-900 flex items-center gap-2">
                                        <Mail size={14} className="text-gray-500" />
                                        <span className="truncate">{authorityData.administrationEmail1 || '—'}</span>
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs text-gray-600">Contact</dt>
                                    <dd className="font-medium text-gray-900 flex items-center gap-2">
                                        <Phone size={14} className="text-gray-500" />
                                        {authorityData.administrationContact1 || '—'}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>

                {/* 7. Services & Specialities */}
                <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 shadow-xl md:col-span-2">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                            <Stethoscope className="w-6 h-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Services & Specialities</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Services */}
                        <div>
                            <div className="text-base font-semibold text-gray-800 mb-3">
                                Services ({selectedServices.length})
                            </div>
                            {selectedServices.length > 0 ? (
                                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                                    {selectedServices.map(id => (
                                        <span
                                            key={id}
                                            className="px-3 py-1.5 bg-primary/5 text-primary text-xs font-medium rounded-lg border border-primary/20"
                                        >
                                            {getFacilityNameById(id)}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500 italic">None selected</p>
                            )}
                        </div>

                        {/* Specialities */}
                        <div>
                            <div className="text-base font-semibold text-gray-800 mb-3">
                                Specialities ({selectedSpecialities.length})
                            </div>
                            {selectedSpecialities.length > 0 ? (
                                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                                    {selectedSpecialities.map(id => (
                                        <span
                                            key={id}
                                            className="px-3 py-1.5 bg-primary/5 text-primary text-xs font-medium rounded-lg border border-primary/20"
                                        >
                                            {getSpecialityNameById(id)}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500 italic">None selected</p>
                            )}
                        </div>
                    </div>
                    {formData.facilityRemarks && (
                        <div>
                            <dt className="text-sm text-gray-600 pt-10">Facility Remarks / Additional Comments</dt>
                            <dd className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg">
                                {formData.facilityRemarks}
                            </dd>
                        </div>
                    )}
                </div>

                {/* 8. Documents Status */}
                <div className="bg-white border-2 border-gray-100 rounded-3xl p-6 shadow-xl">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                            <Upload className="w-6 h-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Documents</h3>
                    </div>
                    <div className="space-y-4">
                        {[
                            { name: 'tradeLicenseFile', label: 'Trade License' },
                            { name: 'tariffListFile', label: 'Tariff List' },
                            { name: 'haLicenseFile', label: 'Health Authority' },
                            { name: 'signatoryEmiratesIdFile', label: 'Signatory ID' },
                            { name: 'otherFiles', label: 'Other Docs' },
                        ].map(doc => {
                            const hasFile = !!formData[doc.name];
                            return (
                                <div key={doc.name} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0">
                                    <span className="text-base text-gray-700">{doc.label}</span>
                                    {hasFile ? (
                                        <span className="flex items-center gap-2 text-primary font-medium">
                                            <Check size={18} />
                                            Selected
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2 text-gray-400 font-medium">
                                            <X size={18} />
                                            Missing
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Final Note */}
            <div className="bg-primary/5 border-2 border-primary/20 rounded-2xl p-6 text-center">
                <p className="text-base text-gray-800">
                    Please confirm all information is accurate. You can go back to any previous step to make changes.
                </p>
            </div>
        </div>
    );
};

export default ReviewStep;